// After Stripe Pay by Bank redirects back to the app, pressing the
// browser's native Back button used to land on the dead external
// bank-authentication page instead of back inside the app. Root cause:
// `popstate` only fires for a same-document history transition — the
// entry behind the return page is a different document (the bank's own
// origin), so the browser tears the current page down before any of our
// JS gets a chance to run on it.
//
// Proven-correct mechanism (see CLAUDE.md's Pay by Bank back-button
// history — several earlier attempts failed): the instant we land on the
// return URL, push one duplicate same-document history entry so the
// *next* Back press stays same-document and fires a real `popstate` we
// can catch, then hard-redirect with `window.location.replace(...)` —
// NOT `$router.replace()`. Vue Router's `currentRoute` is never updated
// by this particular Back transition even though `window.location` is,
// so anything routed through the router (guards, `afterEach`) silently
// no-ops for it.
//
// MUST be armed synchronously, at module-eval time, before Vue mounts and
// before any awaited call. Every failed attempt armed the guard only
// after an awaited network call (e.g. inside a component's mounted() hook
// that polls the backend to confirm the payment) — that leaves a
// multi-second window right after page load where Back is unguarded,
// which is exactly when a real user presses it. This is also why
// automated browser tests kept passing while manual testing kept
// failing: tool round-trips leave multi-second gaps before the Back press
// that a human at the keyboard doesn't.
const SHIELD_SESSION_FLAG = "ucc-pay-by-bank-shield-active";

function isPayByBankReturnUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.has("payment_intent") || params.has("redirect_status");
  } catch {
    return false;
  }
}

// Call synchronously at the very top of an entrypoint's main.js, before
// `new Vue(...).$mount()` and before any awaited call. `fallbackUrl` is
// where the guarded Back press should land — pass the app's own
// resume/landing page for the Pay by Bank flow being guarded (e.g.
// "/client/portal/bookings" or "/dashboard/manual-payment").
export function initPayByBankBackGuard(fallbackUrl) {
  let shouldArm;
  try {
    shouldArm =
      isPayByBankReturnUrl() ||
      sessionStorage.getItem(SHIELD_SESSION_FLAG) === "true";
  } catch {
    // sessionStorage can throw in some privacy modes — never block app
    // boot over a defensive feature.
    return;
  }
  if (!shouldArm) return;

  try {
    // Re-armed on every subsequent page load (this flag persists across
    // the location.replace() full reload below) so protection survives
    // indefinitely, not just for the single return page — a rapid double
    // Back press can still reach a stale bank-origin entry further back
    // in the stack, and the fallback page re-arming itself here is what
    // catches that case too.
    sessionStorage.setItem(SHIELD_SESSION_FLAG, "true");
  } catch {
    // ignore — worst case this guard only covers the current page load
  }

  const resolvedFallback = fallbackUrl || window.location.pathname;
  // Set the instant a guarded Back press is actually navigating this tab
  // away. `event.state` alone can't tell us this: it reflects the
  // DESTINATION history entry, not what's being left, so it can't
  // distinguish "the user just left our guarded entry" from any other
  // popstate shaped like it.
  let leavingGuardedEntry = false;

  const arm = () => {
    window.history.pushState(
      { __payByBankGuard: true },
      "",
      window.location.href
    );
  };

  const onPopState = () => {
    if (leavingGuardedEntry) return;
    leavingGuardedEntry = true;
    window.location.replace(resolvedFallback);
  };

  window.addEventListener("popstate", onPopState);
  // bfcache restore: Safari/Firefox can serve this exact page back out of
  // the back-forward cache without re-running this module's top-level
  // code, so a second rapid Back press needs the guard re-armed here too.
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      leavingGuardedEntry = false;
      arm();
    }
  });

  arm();
}
